import {
  applyPublicCommission,
  resolveCategoryCommission,
  emitPublicPriceSourceTrace,
  DEFAULT_PUBLIC_SITE_COMMISSION_PERCENT,
} from "./publicSitePricingCommission";
import { hrDebugTrace } from "./hrDebugFlags";
import {
  applyAgentHotelLayerToIlsAmount,
  logPublicPriceBaseTraceAfterPublicCommission,
} from "./publicAgentLayeredPricing";

function positiveNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * LDS engine amount (priceWithCommissionAndCreditCardFee) → שכבת HR כמו במערכת הסוכן → עמלת אתר ציבורית.
 */
/**
 * מזהה חדר(ים) נבחר(ים) מ־roomsAndPaxInfo — חייב להתאים ל־roomId ב־publicPackagePriceSnapshot
 */
export function resolveSelectedRoomKeyFromPax(roomsAndPaxInfo) {
  if (!Array.isArray(roomsAndPaxInfo) || roomsAndPaxInfo.length === 0) return null;
  const parts = roomsAndPaxInfo
    .map((p) => p?.room?.["@seqNum"] ?? p?.room?.roomId ?? p?.room?.RoomId)
    .filter((x) => x != null && String(x) !== "");
  if (parts.length === 0) return null;
  return parts.map(String).join("+");
}

export function sumEngineBaseIlsFromSelectedRooms(roomsAndPaxInfo) {
  if (!Array.isArray(roomsAndPaxInfo) || roomsAndPaxInfo.length === 0) return null;
  let sum = 0;
  let any = false;
  for (const p of roomsAndPaxInfo) {
    const v = Number(p?.room?.ourFareDetails?.ourTotalFare?.priceWithCommissionAndCreditCardFee);
    if (Number.isFinite(v) && v > 0) {
      any = true;
      sum += v;
    }
  }
  return any ? sum : null;
}

export function getPublicPackagePriceBreakdownFromOurTotalFare(
  ourTotalFare,
  publicSitePricingSettings = null,
  agentHotelPricing = null,
  traceMeta = null
) {
  const supplierNet = positiveNumber(ourTotalFare?.initialPrice);
  const hrCommissionRaw = Number(ourTotalFare?.hrCommission);
  const hrCommission =
    Number.isFinite(hrCommissionRaw) && hrCommissionRaw >= 0 ? hrCommissionRaw : null;
  const engineBaseIls =
    supplierNet != null && hrCommission != null
      ? supplierNet + hrCommission
      : positiveNumber(ourTotalFare?.priceWithCommissionAndCreditCardFee);
  if (engineBaseIls == null) return null;

  const { agentFinalPrice, hotelHrCommissionPercentage, fixedHotelCommission, pricingBlocked } =
    applyAgentHotelLayerToIlsAmount({
      ilsAmount: engineBaseIls,
      agentHotelPricing,
      sourceFunction: traceMeta?.sourceFunction ?? "getPublicPackagePriceBreakdownFromOurTotalFare",
      packageId: traceMeta?.packageId ?? null,
      hotelId: traceMeta?.hotelId ?? null,
      roomId: traceMeta?.roomId ?? null,
      rawSupplierAmount: traceMeta?.rawSupplierAmount ?? null,
      suppressTrace: true,
    });

  if (
    pricingBlocked ||
    agentFinalPrice == null ||
    !Number.isFinite(agentFinalPrice)
  ) {
    return null;
  }

  const prePublicIls = agentFinalPrice;

  const publicCommission = resolveCategoryCommission(
    publicSitePricingSettings,
    "vacation_package"
  );
  const finalIls = applyPublicCommission(prePublicIls, publicCommission, {
    agentDisplayedPrice: prePublicIls,
    rawBaseBeforePublic: engineBaseIls,
    commissionCategory: "vacation_package",
    sourceSettings: publicSitePricingSettings,
    sourceFunction: traceMeta?.sourceFunction ?? "getPublicPackagePriceBreakdownFromOurTotalFare",
    hypothesisId: "H_package_price_breakdown",
  });

  logPublicPriceBaseTraceAfterPublicCommission({
    rawSupplierAmount: traceMeta?.rawSupplierAmount ?? null,
    convertedAmount: engineBaseIls,
    agentHrCommissionPercent: hotelHrCommissionPercentage,
    fixedHotelCommission,
    agentFinalPrice: prePublicIls,
    publicCommission,
    publicFinalPrice: finalIls,
    sourceFunction: traceMeta?.sourceFunction ?? "getPublicPackagePriceBreakdownFromOurTotalFare",
    packageId: traceMeta?.packageId ?? null,
    hotelId: traceMeta?.hotelId ?? null,
    roomId: traceMeta?.roomId ?? null,
  });

  try {
    const settings = publicSitePricingSettings;
    const explicitVp =
      settings &&
      typeof settings === "object" &&
      settings.vacation_package &&
      typeof settings.vacation_package === "object" &&
      (Object.prototype.hasOwnProperty.call(settings.vacation_package, "type") ||
        Object.prototype.hasOwnProperty.call(settings.vacation_package, "value"));
    emitPublicPriceSourceTrace({
      hypothesisId: "H_price_source_trace_package_breakdown",
      componentName: traceMeta?.sourceFunction ?? "getPublicPackagePriceBreakdownFromOurTotalFare",
      packageId: traceMeta?.packageId ?? null,
      hotelName: traceMeta?.hotelName ?? null,
      agentPrice: prePublicIls,
      sourcePrice: engineBaseIls,
      pricingModel: "engine_hr_then_public",
      publicPackagePriceSnapshot: null,
      publicSitePricingSettings: settings,
      resolvedCategory: "vacation_package",
      resolvedCommissionType: publicCommission?.type ?? null,
      resolvedCommissionValue: publicCommission?.value != null ? Number(publicCommission.value) : null,
      fallbackUsed:
        publicCommission?.type === "percent" &&
        Number(publicCommission?.value) === DEFAULT_PUBLIC_SITE_COMMISSION_PERCENT &&
        !explicitVp,
      finalDisplayedPrice: finalIls,
    });
  } catch {
    /* ignore */
  }

  return {
    engineBaseIls,
    baseIls: prePublicIls,
    publicCommission,
    finalIls,
  };
}

/**
 * @param {string} source — סימון מקור ל-trace
 */
export function logPublicCommissionFlowTrace({
  component,
  baseIls,
  publicCommission,
  finalIls,
  usdDisplay,
  source = "publicVacationPackage",
}) {
  try {
    hrDebugTrace("[PUBLIC_COMMISSION_FLOW_TRACE]", {
      component,
      baseIls,
      commissionType: publicCommission?.type ?? null,
      commissionValue: publicCommission?.value != null ? Number(publicCommission.value) : null,
      finalIls,
      usdDisplay,
      source,
    });
  } catch {
    /* ignore */
  }
}

export function logRoomSelectionPriceTrace(payload) {
  try {
    hrDebugTrace("[ROOM_SELECTION_PRICE_TRACE]", payload);
  } catch {
    /* ignore */
  }
}

export function getPublicVacationPackageDisplayPrice(
  item,
  {
    calculateLDSDealFareForRoomsComposition,
    publicSitePricingSettings = null,
    agentHotelPricing = null,
    source = "initial-navigation",
  } = {}
) {
  if (typeof calculateLDSDealFareForRoomsComposition !== "function") return null;

  const fullDeal = item?.fullDeal || item;
  if (!fullDeal) return null;

  try {
    const fare = calculateLDSDealFareForRoomsComposition(
      fullDeal,
      item?.shortenedDeal
    );
    const br = getPublicPackagePriceBreakdownFromOurTotalFare(
      fare?.ourTotalFare,
      publicSitePricingSettings,
      agentHotelPricing,
      {
        sourceFunction: `getPublicVacationPackageDisplayPrice:${source}`,
        packageId: fullDeal?.dealId ?? item?.dealId ?? null,
        hotelId:
          fullDeal?.hotel?.hotelId ??
          fullDeal?.hotel?.HotelId ??
          item?.hotel?.hotelId ??
          null,
        hotelName: fullDeal?.hotel?.hotelName ?? fullDeal?.hotel?.hotelNameEn ?? null,
        roomId: fullDeal?.selectedRoom?.roomId ?? fullDeal?.selectedRoom?.RoomId ?? null,
      }
    );
    if (br == null) return null;
    const { baseIls: basePrice, publicCommission, finalIls: displayPrice } = br;
    return {
      basePrice,
      displayPrice,
      publicCommission,
      fare,
    };
  } catch {
    return null;
  }
}

